import { DateTime } from 'luxon';

import { FilterComparisonOperator, MediaType } from '@/common/models';
import Filter from '@/common/models/query-params/Filter';
import LogicalOperator from '@/common/models/query-params/FilterLogicalOperator.enum';
import { FilterGroup, QueryParams } from '@/common/models/query-params/index';
import { areArraysEqual } from '@/globals';
import Validatable from '@/validator/Validatable';

class ListFilter extends Validatable {
  type: string;
  property: string;
  label: string;
  valuesInternal: Array<string | number>;
  excludedValuesInternal: Array<string | number>;
  source: Array<any> | (() => Array<any>) | Promise<any>;
  sourceFilterProperty: string;
  sourceLabel: string;
  sourceID: string;
  checkForEmptyness: boolean;
  customQueryParams: (values: Array<string | number>, comparisonOperator: string) => string;
  description: string;
  isLoadingOptions: boolean;
  isDisabled: boolean;
  valuesChangedExternallyAt: DateTime;

  private static cache: Record<string, Array<any>> = {};
  private instance: any;

  constructor(
    property = '',
    label = '',
    valuesInternal = [],
    excludedValuesInternal = [],
    source = null,
    sourceFilterProperty = null,
    sourceLabel = 'name',
    sourceID = 'id',
    checkForEmptyness = false,
    customQueryParams = null,
    isDisabled = false,
  ) {
    super();
    this.type = 'ListFilter';
    this.property = property;
    this.label = label;
    this.valuesInternal = valuesInternal;
    this.excludedValuesInternal = excludedValuesInternal;
    this.source = source;
    this.sourceFilterProperty = sourceFilterProperty;
    this.sourceLabel = sourceLabel;
    this.sourceID = sourceID;
    this.checkForEmptyness = checkForEmptyness;
    this.customQueryParams = customQueryParams;
    this.isDisabled = isDisabled;

    this.description = null;
    this.isLoadingOptions = true;
    this.valuesChangedExternallyAt = null;
    this.loadOptions();
  }

  get isEmpty() {
    return (
      (this.valuesInternal == null || this.valuesInternal.length === 0) && this.excludedValuesInternal?.length === 0
    );
  }

  get optionsCached() {
    if (this.source instanceof Array) return this.source;
    return ListFilter.cache[this.property];
  }

  findOptionInSource(value) {
    if (this.optionsCached == null) return null;

    // HV-9197 - We have to make an exception for media types, because they are nested and we have to search recursively
    const isMediaTypesSource =
      this.sourceFilterProperty === 'mediatype_id' && this.optionsCached.every((option) => option instanceof MediaType);
    if (isMediaTypesSource) {
      return this.optionsCached.find((o) => this.findOptionInMediaType(o, value));
    }
    return this.optionsCached.find((o) => o[this.sourceID] == value);
  }

  /**
   * Find option in media type children recursively
   * @param {MediaType} mediaType
   * @param value
   * @returns {MediaType | null}
   */
  findOptionInMediaType(mediaType, value) {
    if (mediaType[this.sourceID] == value) {
      return mediaType;
    }
    for (const child of mediaType.children) {
      const result = this.findOptionInMediaType(child, value);
      if (result != null) {
        return result;
      }
    }
    return null;
  }

  get selectedOptions() {
    return this.valuesInternal.map((value) => this.findOptionInSource(value)).filter((value) => value != null);
  }

  get excludedOptions() {
    return this.excludedValuesInternal.map((value) => this.findOptionInSource(value)).filter((value) => value != null);
  }

  get includedAndExcludedOptions() {
    return [...this.selectedOptions, ...this.excludedOptions];
  }

  get values() {
    return [...this.valuesInternal];
  }

  set values(val) {
    if (!areArraysEqual(val, this.valuesInternal)) {
      this.valuesInternal = [...val];
    }
  }

  get excludedValues() {
    return [...this.excludedValuesInternal];
  }

  set excludedValues(val) {
    if (!areArraysEqual(val, this.excludedValuesInternal)) {
      this.excludedValuesInternal = [...val];
    }
  }

  async loadOptions() {
    if (this.isLoadingOptions) {
      ListFilter.cache[this.property] = null;
      if (this.source instanceof Function) {
        const queryParams = QueryParams.all();
        queryParams.showLoader = false;
        const sourceResponse = await queryParams.run(this.source);
        ListFilter.cache[this.property] = sourceResponse.items || sourceResponse;
      } else if (this.source instanceof Promise) {
        ListFilter.cache[this.property] = await this.source;
      } else {
        ListFilter.cache[this.property] = this.source;
      }
    }
    this.isLoadingOptions = false;
  }

  get options() {
    return this.optionsCached;
  }

  static fromObject(obj) {
    return super.fromObject(obj, new ListFilter());
  }

  getFilters() {
    if (this.valuesInternal.length === 0 && this.excludedValuesInternal.length === 0) return [];
    if (this.selectedOptions.some((x) => x?.filters != null)) {
      return [
        new FilterGroup(
          this.selectedOptions.map(
            (x) =>
              new FilterGroup(
                (x.filters || []).length > 0
                  ? x.filters
                  : [
                      new Filter(
                        this.sourceFilterProperty || this.property,
                        [x[this.sourceID]],
                        FilterComparisonOperator.EQUAL,
                        LogicalOperator.OR,
                        null,
                        this.customQueryParams,
                      ),
                    ],
                LogicalOperator.AND,
              ),
          ),
          LogicalOperator.OR,
        ),
      ];
    }

    if (this.excludedOptions.some((x) => x?.filters != null)) {
      return [
        new FilterGroup(
          this.selectedOptions.map(
            (x) =>
              new FilterGroup(
                x.filters || [
                  new Filter(
                    this.sourceFilterProperty || this.property,
                    [x[this.sourceID]],
                    FilterComparisonOperator.NOT_EQUAL,
                    LogicalOperator.OR,
                    null,
                    this.customQueryParams,
                  ),
                ],
                LogicalOperator.AND,
              ),
          ),
          LogicalOperator.OR,
        ),
      ];
    }
    return [
      ...(this.valuesInternal.length > 0
        ? [
            new Filter(
              this.sourceFilterProperty || this.property,
              this.valuesInternal,
              FilterComparisonOperator.IN,
              LogicalOperator.OR,
              null,
              this.customQueryParams,
            ),
          ]
        : []),
      ...(this.excludedValuesInternal.length > 0
        ? [
            new Filter(
              this.sourceFilterProperty || this.property,
              this.excludedValuesInternal,
              FilterComparisonOperator.NOT_IN,
              LogicalOperator.OR,
              null,
              this.customQueryParams,
            ),
          ]
        : []),
    ];
  }

  clear() {
    this.valuesInternal = [];
    this.excludedValuesInternal = [];
  }

  serializeForQueryString() {
    const included = this.valuesInternal != null ? this.valuesInternal.join('.') : '';
    const excluded = this.excludedValuesInternal != null ? this.excludedValuesInternal.join('.') : '';
    return `${this.property}~${included}~${excluded}`;
  }

  static deserializeFromQueryString(filter, serialized) {
    const fragments = serialized.split('~');
    const values = fragments[1] != null ? fragments[1].split('.').filter((x) => x.trim() !== '') : [];
    const excludedValues = fragments[2] != null ? fragments[2].split('.').filter((x) => x.trim() !== '') : [];
    return new ListFilter(
      filter.property,
      filter.label,
      values,
      excludedValues,
      filter.source,
      filter.sourceFilterProperty,
      filter.sourceLabel,
      filter.sourceID,
      filter.checkForEmptyness,
      filter.customQueryParams,
      filter.isDisabled,
    );
  }

  toApi() {
    return {
      type: this.type,
      property: this.property,
      label: this.label,
      values_internal: this.valuesInternal,
      exluded_values_internal: this.excludedValuesInternal,
      source: this.source,
      source_filter_property: this.sourceFilterProperty,
      source_label: this.sourceLabel,
      source_id: this.sourceID,
      check_for_emptyness: this.checkForEmptyness,
      custom_query_params: this.customQueryParams,
      is_disabled: this.isDisabled,
    };
  }

  static fromApi(apiObject) {
    const filter = new ListFilter(
      apiObject.property,
      apiObject.label,
      apiObject.values_internal,
      apiObject.exluded_values_internal,
      apiObject.source,
      apiObject.source_filter_property,
      apiObject.source_label,
      apiObject.source_id,
      apiObject.check_for_emptyness,
      apiObject.custom_query_params,
      apiObject.is_disabled,
    );
    filter.instance = apiObject.instance;
    return filter;
  }
}

export default ListFilter;
